import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, Select, MenuItem, Divider, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, Autocomplete, CircularProgress
} from '@mui/material';
import { ArrowBack, Add, Delete, Save, Warning, ExpandMore, ExpandLess } from '@mui/icons-material';
import LogoLoader from '../../common/LogoLoader';
import './SchemeForm.css';

const SchemeForm = () => {
  useSessionValidation();
  const history = useHistory();
  const { id, action } = useParams();
  const { companyId, branchId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();

  const isViewMode = action === 'view';
  const isEditMode = action === 'edit';
  const isCreateMode = action === 'create';
  const title = isCreateMode ? 'New Scheme' : isEditMode ? 'Edit Scheme' : 'View Scheme';

  // States
  const [formData, setFormData] = useState({});
  const [schemeDetails, setSchemeDetails] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Per-row dynamic item lists
  const [rowItems, setRowItems] = useState({});
  const [rowItemsLoading, setRowItemsLoading] = useState({});
  // Back confirmation
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [backConfirmOpen, setBackConfirmOpen] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [originalDetails, setOriginalDetails] = useState([]);

  // Supplier section expand/collapse
  const [supplierExpanded, setSupplierExpanded] = useState(true);
  const [supplierFound, setSupplierFound] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const fetchRowItems = useCallback((index, brandId, categoryId) => {
    if (!brandId && !categoryId) { setRowItems(prev => ({ ...prev, [index]: [] })); return; }
    const bId = brandId || 0;
    const cId = categoryId || 0;
    setRowItemsLoading(prev => ({ ...prev, [index]: true }));
    fetchData('SchemeApi', `/BrandAndCategoryWiseItemList/${companyId}/${branchId}/${bId}/${cId}`, token)
      .then(res => setRowItems(prev => ({ ...prev, [index]: res.response_data || res || [] })))
      .catch(() => setRowItems(prev => ({ ...prev, [index]: [] })))
      .finally(() => setRowItemsLoading(prev => ({ ...prev, [index]: false })));
  }, [companyId, branchId, token]);

  useEffect(() => {
    if (companyId && token) {
      loadData();
    }
  }, [companyId, token, id, action]);

  const loadData = async () => {
    setLoading(true);
    setLoadingMessage('Loading...');

    // Fetch brands, categories, suppliers (SchemeApi/Party)
    try {
      const [brandsRes, catsRes, suppRes] = await Promise.allSettled([
        fetchData('BrandApi', `/${companyId}`, token),
        fetchData('CategoryApi', `/${companyId}`, token),
        fetchData('SchemeApi', `/Party/${companyId}/${branchId}`, token),
      ]);
      setBrands(brandsRes.status === 'fulfilled' ? (brandsRes.value.response_data || brandsRes.value || []) : []);
      setCategories(catsRes.status === 'fulfilled' ? (catsRes.value.response_data || catsRes.value || []) : []);
      setSuppliers(suppRes.status === 'fulfilled' ? (suppRes.value.response_data || suppRes.value || []) : []);
    } catch (e) {
      console.error('Error fetching data:', e);
    }

    if (isCreateMode) {
      try {
        const response = await fetchData('SchemeApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
        const data = response.response_data || response || {};
        const billNumber = data.BillNumber || data.billNumber || data.SchemeNumber || 1;
        const finYear = data.FinYear || data.finYear || '';
        let schemeNo = finYear ? `${billNumber}/${finYear}` : `${billNumber}`;

        const newForm = {
          CompanyId: companyId, BranchId: branchId || 1, MasterId: null,
          SchemeNumber: billNumber, SchemeNo: schemeNo, FinYear: finYear,
          SchemeStartDate: new Date().toISOString().split('T')[0],
          SchemeEndDate: '',
          SupplierName: '', ContactNumber: '', Address: '', Remarks: '',
          IsActive: true
        };
        setFormData(newForm);
        setOriginalFormData(newForm);
        setSchemeDetails([]);
        setOriginalDetails([]);
        setRowItems({});
        setRowItemsLoading({});
      } catch (e) {
        console.error('Error fetching max bill:', e);
      }
    } else if (id) {
      try {
        const response = await fetchData('SchemeApi', `/${id}`, token);
        const freshData = response.response_data || response;
        const schemeData = freshData.Scheme || freshData;
        const details = freshData.SchemeDetails || freshData.Details || [];

        const form = {
          Id: schemeData.Id, CompanyId: schemeData.CompanyId || companyId,
          BranchId: schemeData.BranchId || branchId, MasterId: schemeData.MasterId || null,
          SchemeNumber: schemeData.SchemeNumber || 0, SchemeNo: schemeData.SchemeNo || '',
          FinYear: schemeData.FinYear || '',
          SchemeStartDate: schemeData.SchemeStartDate?.split('T')[0] || '',
          SchemeEndDate: schemeData.SchemeEndDate?.split('T')[0] || '',
          SupplierName: schemeData.SupplierName || '', ContactNumber: schemeData.ContactNumber || '',
          Address: schemeData.Address || '', Remarks: schemeData.Remarks || '',
          IsActive: schemeData.IsActive ?? true,
          CreatedBy: schemeData.CreatedBy ?? null,
          CreatedDate: schemeData.CreatedDate ?? null,
        };
        setFormData(form);
        setOriginalFormData(form);

        const mappedDetails = details.map(d => ({
          Id: d.Id || 0, BrandId: d.BrandId || null, CategoryId: d.CategoryId || null,
          ItemId: d.ItemId || null, IsTarget: d.IsTarget || 1,
          Quantity: d.Quantity || 0, Rate: d.Rate || 0,
          Description: d.Description || '', IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy ?? null,
          CreatedDate: d.CreatedDate ?? null,
          ItemName: d.ItemName || null,
          BrandName: d.BrandName || null,
          CategoryName: d.CategoryName || null,
        }));
        setSchemeDetails(mappedDetails);
        setOriginalDetails(mappedDetails);

        // Pre-fetch item lists for existing rows
        const newRowItems = {};
        await Promise.allSettled(
          mappedDetails.map((d, index) => {
            if (d.BrandId || d.CategoryId) {
              return fetchData('SchemeApi', `/BrandAndCategoryWiseItemList/${companyId}/${branchId}/${d.BrandId || 0}/${d.CategoryId || 0}`, token)
                .then(res => { newRowItems[index] = res.response_data || res || []; })
                .catch(() => { newRowItems[index] = []; });
            }
            return Promise.resolve();
          })
        );
        setRowItems(newRowItems);
        setRowItemsLoading({});
      } catch (e) {
        console.error('Error fetching scheme:', e);
        setError('Error loading scheme');
      }
    }
    setLoading(false);
  };

  // Track changes
  const changeTrackRef = useRef(null);
  useEffect(() => {
    if (!isViewMode && Object.keys(originalFormData).length > 0) {
      clearTimeout(changeTrackRef.current);
      changeTrackRef.current = setTimeout(() => {
        const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
        const hasDetailChanges = JSON.stringify(schemeDetails) !== JSON.stringify(originalDetails);
        setHasUnsavedChanges(hasFormChanges || hasDetailChanges);
      }, 300);
    }
    return () => clearTimeout(changeTrackRef.current);
  }, [formData, schemeDetails, originalFormData, originalDetails, isViewMode]);

  const handleBack = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setBackConfirmOpen(true);
    } else {
      history.push('/SchemeIndex');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectSupplier = (event, supplier) => {
    if (supplier) {
      const addressParts = [
        supplier.AddressLine1 || supplier.Address1 || supplier.Address || '',
        supplier.AddressLine2 || supplier.Address2 || '',
        supplier.Location || supplier.City || '',
        supplier.Pin || supplier.Pincode || supplier.PostalCode || ''
      ].filter(Boolean);
      const fullAddress = addressParts.join(', ');
      setFormData(prev => ({
        ...prev,
        SupplierName: supplier.Name || supplier.SupplierName || '',
        ContactNumber: supplier.ContactNumber1 || supplier.ContactNumber || supplier.Mobile || '',
        Address: fullAddress,
        MasterId: supplier.MasterId || supplier.Id || null
      }));
      setSelectedSupplier(supplier);
      setSupplierFound(true);
    } else {
      setFormData(prev => ({ ...prev, SupplierName: '', ContactNumber: '', Address: '', MasterId: null }));
      setSelectedSupplier(null);
      setSupplierFound(false);
    }
  };

  const handleDetailChange = useCallback((index, field, value) => {
    const updatedDetails = [...schemeDetails];
    if (['Quantity', 'Rate'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        setSchemeDetails(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
    }
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (field === 'IsTarget') {
      const targetVal = parseInt(value);
      if (targetVal === 1) updatedDetails[index].Rate = 0;
      else if (targetVal === 2) updatedDetails[index].Quantity = 0;
    }

    // BrandId/CategoryId change → reset ItemId, fetch new item list
    if (field === 'BrandId' || field === 'CategoryId') {
      updatedDetails[index].ItemId = null;
      const newBrandId = field === 'BrandId' ? value : updatedDetails[index].BrandId;
      const newCategoryId = field === 'CategoryId' ? value : updatedDetails[index].CategoryId;
      fetchRowItems(index, newBrandId, newCategoryId);
    }

    setSchemeDetails(updatedDetails);
  }, [schemeDetails, fetchRowItems]);

  const addDetailRow = () => {
    setSchemeDetails(prev => [...prev, {
      Id: 0, BrandId: null, CategoryId: null, ItemId: null,
      IsTarget: 1, Quantity: 0, Rate: 0, Description: '', IsActive: true
    }]);
  };

  const removeDetailRow = useCallback((index) => {
    setSchemeDetails(prev => prev.filter((_, i) => i !== index));
    setRowItems(prev => {
      const updated = {};
      Object.keys(prev).forEach(k => {
        const ki = parseInt(k);
        if (ki < index) updated[ki] = prev[k];
        else if (ki > index) updated[ki - 1] = prev[k];
      });
      return updated;
    });
  }, []);

  // Maps
  const brandNameMap = useMemo(() => {
    const map = {};
    brands.forEach(b => { map[b.Id] = b.Name; });
    return map;
  }, [brands]);

  const categoryNameMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.Id] = c.Name; });
    return map;
  }, [categories]);

  const handleSave = async () => {
    if (!formData.SchemeEndDate) { setError('Scheme End Date is required'); return; }
    if (formData.SchemeStartDate && formData.SchemeEndDate && formData.SchemeEndDate < formData.SchemeStartDate) { setError('End Date cannot be before Start Date'); return; }
    if (!formData.SupplierName?.trim()) { setError('Supplier Name is required'); return; }
    if (schemeDetails.length === 0) { setError('Please add at least one scheme detail'); return; }
    // Validate: remove blank rows (no item selected) and check qty/rate
    const validDetails = schemeDetails.filter(d => d.ItemId);
    if (validDetails.length === 0) { setError('Please select at least one item in scheme details'); return; }
    const invalidQtyRow = validDetails.find(d => d.IsTarget === 1 && (!d.Quantity || parseFloat(d.Quantity) <= 0));
    if (invalidQtyRow) { setError('Quantity is required for all items with Target type "Qty"'); return; }
    const invalidRateRow = validDetails.find(d => d.IsTarget === 2 && (!d.Rate || parseFloat(d.Rate) <= 0));
    if (invalidRateRow) { setError('Rate is required for all items with Target type "Rate"'); return; }
    setSchemeDetails(validDetails);

    setLoading(true);
    setLoadingMessage('Saving scheme...');

    const now = new Date();
    const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, -1);

    try {
      const payload = {
        Scheme: {
          ...formData, CompanyId: companyId, BranchId: branchId || 1,
          CreatedBy: isCreateMode ? userId : formData.CreatedBy, ModifiedBy: userId,
          CreatedDate: isCreateMode ? localNow : formData.CreatedDate,
          ModifiedDate: localNow,
        },
        SchemeDetails: schemeDetails.map(d => ({
          ...d, CompanyId: companyId, BranchId: branchId || 1,
          SchemeId: formData.Id || 0,
          Quantity: d.IsTarget === 1 ? (parseFloat(d.Quantity) || 0) : 0,
          Rate: d.IsTarget === 2 ? (parseFloat(d.Rate) || 0) : 0,
          CreatedBy: isCreateMode ? userId : (d.CreatedBy ?? null),
          CreatedDate: isCreateMode ? localNow : (d.CreatedDate ?? null),
          ModifiedBy: userId, ModifiedDate: localNow,
          ItemName: undefined, BrandName: undefined, CategoryName: undefined,
        }))
      };

      if (isCreateMode) {
        await postData('SchemeApi', '', payload, token);
        setSuccess('Scheme created successfully!');
      logAudit(AUDIT_ACTION.CREATE, 'SchemeApi', 0, null, { SchemeNo: formData.SchemeNo || '', SupplierName: formData.SupplierName || '' });
      } else {
        await updateData('SchemeApi', formData.Id, payload, token);
        setSuccess('Scheme updated successfully!');
      logAudit(AUDIT_ACTION.UPDATE, 'SchemeApi', formData.Id, { ...originalFormData }, { ...formData });
      }
      setTimeout(() => history.push('/SchemeIndex'), 1000);
    } catch (e) {
      console.error('Error saving scheme:', e);
      setError(e.response?.data?.message || 'Error saving scheme');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get display name for detail row
  const getDetailDisplayName = (detail, index) => {
    if (detail.ItemId) {
      const fromRowItems = (rowItems[index] || []).find(i => i.Id === detail.ItemId);
      if (fromRowItems) return fromRowItems.Code ? `${fromRowItems.Code} - ${fromRowItems.Name}` : fromRowItems.Name;
      if (detail.ItemName) return detail.ItemName;
      return 'Item';
    }
    if (detail.BrandId) return `Brand: ${brandNameMap[detail.BrandId] || detail.BrandName || '-'}`;
    if (detail.CategoryId) return `Category: ${categoryNameMap[detail.CategoryId] || detail.CategoryName || '-'}`;
    return 'Not selected';
  };

  if (loading) return <LogoLoader message={loadingMessage} />;

  return (
    <div className="scheme-form-page">
      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      {/* Top Header Bar */}
      <div className="scheme-form-header">
        <div className="scheme-form-header-left">
          <IconButton size="small" onClick={handleBack} sx={{ color: '#7c3aed' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
            {title}
          </Typography>
          {hasUnsavedChanges && !isViewMode && (
            <Chip label="Unsaved" size="small" sx={{ fontSize: '0.6rem', height: '18px', bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }} />
          )}
        </div>
        {!isViewMode && (
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave}
            sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, borderRadius: '8px' }}>
            Save
          </Button>
        )}
      </div>

      {/* Form Content */}
      <div className="scheme-form-content">
        {/* Scheme Info */}
        <div className="scheme-form-section">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setSupplierExpanded(!supplierExpanded)}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed' }}>
              SCHEME INFO
            </Typography>
            <IconButton size="small" sx={{ p: 0.3 }}>
              {supplierExpanded ? <ExpandLess sx={{ fontSize: 18, color: '#64748b' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#64748b' }} />}
            </IconButton>
          </Box>

          {/* Always show Scheme No & Date */}
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>No:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed' }}>{formData.SchemeNo || '-'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, justifyContent: 'flex-end' }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Start:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>{formData.SchemeStartDate || '-'}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Collapsible fields */}
          {supplierExpanded && (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="Start Date" value={formData.SchemeStartDate || ''}
                  disabled={isViewMode} onChange={(e) => handleChange('SchemeStartDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={!isViewMode && !!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeStartDate > formData.SchemeEndDate}
                  helperText={!isViewMode && !!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeStartDate > formData.SchemeEndDate ? 'Start Date cannot be after End Date' : ''}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="End Date *" value={formData.SchemeEndDate || ''}
                  disabled={isViewMode} onChange={(e) => handleChange('SchemeEndDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!isViewMode && (!formData.SchemeEndDate || (!!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeEndDate < formData.SchemeStartDate))}
                  helperText={!isViewMode && !formData.SchemeEndDate ? 'End Date is required' : (!isViewMode && !!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeEndDate < formData.SchemeStartDate ? 'End Date cannot be before Start Date' : '')}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1 } }} />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={suppliers}
                  value={selectedSupplier}
                  onChange={handleSelectSupplier}
                  disabled={isViewMode}
                  getOptionLabel={(option) => option.Name || option.SupplierName || ''}
                  filterOptions={(options, { inputValue }) => {
                    const term = inputValue.trim().toLowerCase();
                    if (!term) return options;
                    return options.filter(s => (s.Name || '').toLowerCase().includes(term));
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ py: 0.5, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>
                        {option.Name || option.SupplierName || '-'}
                      </Typography>
                    </Box>
                  )}
                  freeSolo
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, SupplierName: value, MasterId: null }));
                      setSupplierFound(false);
                      setSelectedSupplier(null);
                    }
                  }}
                  inputValue={formData.SupplierName || ''}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Supplier Name *"
                      placeholder="Search supplier..."
                      helperText={supplierFound ? '✓ Supplier selected' : ''}
                      FormHelperTextProps={{ sx: { color: '#059669', fontSize: '0.7rem', mt: 0.3 } }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} />
                  )}
                  noOptionsText="Not found"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''}
                  disabled={isViewMode} onChange={(e) => handleChange('Remarks', e.target.value)}
                  multiline minRows={2}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1 } }} />
              </Grid>
            </Grid>
          )}
        </div>

        <Divider sx={{ my: 1 }} />

        {/* Scheme Details */}
        <div className="scheme-form-section">
          <div className="scheme-items-header">
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed' }}>
              SCHEME DETAILS ({schemeDetails.length})
            </Typography>
            {!isViewMode && (
              <Button size="small" variant="contained" startIcon={<Add />} onClick={addDetailRow}
                sx={{ fontSize: '0.75rem', py: 0.5, textTransform: 'none', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, borderRadius: '8px' }}>
                Add Row
              </Button>
            )}
          </div>

          {/* Detail Cards */}
          <div className="scheme-items-list">
            {schemeDetails.length === 0 ? (
              <div className="scheme-empty-items">
                <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 3 }}>
                  No details added. Tap "Add Row" to add.
                </Typography>
              </div>
            ) : (
              schemeDetails.map((detail, index) => (
                <Box key={index} sx={{
                  border: '1px solid #e2e8f0', borderRadius: '12px', mb: 1.5,
                  overflow: 'hidden', bgcolor: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  {/* Card Header */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.5, py: 1, bgcolor: '#f8f7ff', borderBottom: '1px solid #ede9fe'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', bgcolor: '#7c3aed',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff' }}>{index + 1}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>
                        {getDetailDisplayName(detail, index)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        px: 1, py: 0.2, borderRadius: '20px', fontSize: '0.6rem', fontWeight: 700,
                        bgcolor: detail.IsTarget === 1 ? '#dcfce7' : '#fef3c7',
                        color: detail.IsTarget === 1 ? '#166534' : '#92400e',
                        border: `1px solid ${detail.IsTarget === 1 ? '#86efac' : '#fcd34d'}`
                      }}>
                        {detail.IsTarget === 1 ? 'QTY' : 'RATE'}
                      </Box>
                      {!isViewMode && (
                        <IconButton size="small" onClick={() => removeDetailRow(index)}
                          sx={{ color: '#dc2626', p: 0.3, '&:hover': { bgcolor: '#fee2e2' } }}>
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Card Body */}
                  <Box sx={{ px: 1.5, py: 1.2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Row 1: Target + Value */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <FormControl size="small" fullWidth disabled={isViewMode}>
                        <Select value={detail.IsTarget || 1}
                          onChange={(e) => handleDetailChange(index, 'IsTarget', e.target.value)}
                          sx={{ fontSize: '0.78rem', borderRadius: '8px', '& .MuiSelect-select': { py: 0.7 } }}>
                          <MenuItem value={1} sx={{ fontSize: '0.78rem' }}>Qty Target</MenuItem>
                          <MenuItem value={2} sx={{ fontSize: '0.78rem' }}>Rate Target</MenuItem>
                        </Select>
                      </FormControl>
                      {detail.IsTarget === 1 ? (
                        <TextField size="small" label="Quantity" type="number"
                          value={detail.Quantity || ''} disabled={isViewMode}
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.7 }, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                      ) : (
                        <TextField size="small" label="Rate" type="number"
                          value={detail.Rate || ''} disabled={isViewMode}
                          onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.7 }, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                      )}
                    </Box>

                    {/* Row 2: Brand + Category */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <FormControl size="small" fullWidth disabled={isViewMode}>
                        <Select value={detail.BrandId || ''} displayEmpty
                          onChange={(e) => handleDetailChange(index, 'BrandId', e.target.value || null)}
                          sx={{ fontSize: '0.78rem', borderRadius: '8px', '& .MuiSelect-select': { py: 0.7 } }}>
                          <MenuItem value=""><em style={{ fontSize: '0.75rem' }}>Brand</em></MenuItem>
                          {brands.map(b => <MenuItem key={b.Id} value={b.Id} sx={{ fontSize: '0.78rem' }}>{b.Name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth disabled={isViewMode}>
                        <Select value={detail.CategoryId || ''} displayEmpty
                          onChange={(e) => handleDetailChange(index, 'CategoryId', e.target.value || null)}
                          sx={{ fontSize: '0.78rem', borderRadius: '8px', '& .MuiSelect-select': { py: 0.7 } }}>
                          <MenuItem value=""><em style={{ fontSize: '0.75rem' }}>Category</em></MenuItem>
                          {categories.map(c => <MenuItem key={c.Id} value={c.Id} sx={{ fontSize: '0.78rem' }}>{c.Name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Row 3: Item */}
                    <FormControl size="small" fullWidth disabled={isViewMode || (!detail.BrandId && !detail.CategoryId)}>
                      <Select value={detail.ItemId || ''} displayEmpty
                        onChange={(e) => handleDetailChange(index, 'ItemId', e.target.value || null)}
                        sx={{ fontSize: '0.78rem', borderRadius: '8px', '& .MuiSelect-select': { py: 0.7 } }}>
                        <MenuItem value="">
                          <em style={{ fontSize: '0.75rem' }}>
                            {!detail.BrandId && !detail.CategoryId ? '— Select brand/category first —' : rowItemsLoading[index] ? 'Loading items...' : 'Item (optional)'}
                          </em>
                        </MenuItem>
                        {(rowItems[index] || []).map(i => (
                          <MenuItem key={i.Id} value={i.Id} sx={{ fontSize: '0.78rem' }}>{i.Code ? `${i.Code} - ` : ''}{i.Name}</MenuItem>
                        ))}
                        {detail.ItemId && detail.ItemName && !(rowItems[index] || []).find(i => i.Id === detail.ItemId) && (
                          <MenuItem value={detail.ItemId} sx={{ fontSize: '0.78rem' }}>{detail.ItemName}</MenuItem>
                        )}
                      </Select>
                    </FormControl>

                    {/* Row 4: Description */}
                    <TextField size="small" label="Description" fullWidth
                      value={detail.Description || ''} disabled={isViewMode}
                      onChange={(e) => handleDetailChange(index, 'Description', e.target.value)}
                      placeholder="Enter description..."
                      multiline minRows={2} maxRows={4}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' }, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                  </Box>
                </Box>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Back Confirmation */}
      <Dialog open={backConfirmOpen} onClose={() => setBackConfirmOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#d97706' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Unsaved Changes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">You have unsaved changes. Discard and go back?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackConfirmOpen(false)} size="small">Stay</Button>
          <Button onClick={() => history.push('/SchemeIndex')} color="error" size="small" variant="contained">Discard</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SchemeForm;
